import React, { useState } from 'react';
import Header from '../../component/layout/Header';
import Footer from '../../component/layout/Footer';
import Page from '../../component/utility/Page';
import DataTable from 'react-data-table-component';
import { custTableStyles } from '../../style/color';
import axios from 'axios';
import { mainurl } from '../../Mainurl';

import { Row, Col, Card, CardBody, Button, Input, Table } from 'reactstrap';
import {FaSearch, FaChartLine, FaPlus, FaMinus} from 'react-icons/fa';

const ProfitLoss = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [dateError, setDateError] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});

    const validateDates = (from, to) => {
        if (from && to && new Date(from) > new Date(to)) {
            setDateError('To Date cannot be earlier than From Date');
            return false;
        }
        setDateError('');
        return true;
    };

    const handleFromDateChange = (e) => {
        const newFromDate = e.target.value;
        setFromDate(newFromDate);
        validateDates(newFromDate, toDate);
    };

    const handleToDateChange = (e) => {
        const newToDate = e.target.value;
        setToDate(newToDate);
        validateDates(fromDate, newToDate);
    };

    const fetchProfitLoss = () => {
        setLoading(true);
        setReportData(null);
        
        let postData = [{
            "org_id": sessionStorage.getItem('org_id'), 
            "user_id": sessionStorage.getItem('user_id'),
            "from_date": fromDate,
            "to_date": toDate
        }];
        
        axios({
            method: 'post',
            url: `${mainurl}Accounts/pl`,
            data: postData,
            headers: { 'Content-Type': 'application/JSON', }
        })
            .then(function (response) {
                console.log('Profit Loss API Response:', response.data);
                
                // Support both nested_pl_report and nested_profit_loss
                const nestedReport = response.data[0].nested_pl_report || response.data[0].nested_profit_loss;
                if (response.data && response.data.length > 0 && nestedReport) {
                    try {
                        const reportJson = JSON.parse(nestedReport);
                        setReportData(reportJson);
                        
                        // Initialize expanded state for all groups exactly as received from API (no deduplication)
                        const initialExpanded = {};
                        const revenueSection = reportJson.structure.revenue_section || reportJson.structure.revenew_section;
                        if (revenueSection) {
                            const revenueGroups = revenueSection.groups || [];
                            revenueGroups.forEach((group, index) => {
                                const uniqueKey = `revenue_${group.group_id}_${index}`;
                                initialExpanded[uniqueKey] = false;
                                // Also initialize for child groups
                                if (group.children) {
                                    initializeChildGroupsExpanded(group.children, 'revenue', initialExpanded, index);
                                }
                            });
                        }
                        if (reportJson.structure.expense_section) {
                            const expenseGroups = reportJson.structure.expense_section.groups || [];
                            expenseGroups.forEach((group, index) => {
                                const uniqueKey = `expense_${group.group_id}_${index}`;
                                initialExpanded[uniqueKey] = false;
                                // Also initialize for child groups
                                if (group.children) {
                                    initializeChildGroupsExpanded(group.children, 'expense', initialExpanded, index);
                                }
                            });
                        }
                        setExpandedGroups(initialExpanded);
                        
                    } catch (error) {
                        console.error('Error parsing nested P&L report:', error);
                        setReportData(null);
                    }
                } else {
                    console.log('No nested report data found');
                    setReportData(null);
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Profit Loss API Error:', error);
                setLoading(false);
                setReportData(null);
            });
    };

    const search_handler = () => {
        // Validate dates before making API call
        if (!fromDate || !toDate) {
            setDateError('Please select both From Date and To Date');
            return;
        }
        
        if (!validateDates(fromDate, toDate)) {
            return;
        }
        
        fetchProfitLoss();
    };

    const reset_handler = () => {
        setFromDate('');
        setToDate('');
        setReportData(null);
        setDateError('');
        setExpandedGroups({});
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹ 0.00';
        return `₹ ${Math.abs(parseFloat(amount)).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };



    // Helper function to initialize expanded state for child groups recursively  
    const initializeChildGroupsExpanded = (children, sectionType, expandedObj, parentIndex = 0) => {
        if (!children || !Array.isArray(children)) return;
        
        children.forEach((childGroup, childIndex) => {
            const uniqueKey = `${sectionType}_${childGroup.group_id}_${parentIndex}_${childIndex}`;
            expandedObj[uniqueKey] = false;
            if (childGroup.children && childGroup.children.length > 0) {
                initializeChildGroupsExpanded(childGroup.children, sectionType, expandedObj, `${parentIndex}_${childIndex}`);
            }
        });
    };

    // Enhanced function to calculate total group amount including all children
    const calculateGroupTotalWithChildren = (group) => {
        if (!group) return 0;
        
        // Start with the group's own total (use absolute value for display)
        let total = Math.abs(parseFloat(group.group_total || 0));
        
        // Add amounts from direct accounts
        if (group.accounts && Array.isArray(group.accounts)) {
            group.accounts.forEach(account => {
                if (account && account.account_id) {
                    // Use the amount field if available, otherwise calculate from debits/credits
                    const accountAmount = account.amount !== null ? 
                        Math.abs(parseFloat(account.amount || 0)) :
                        Math.abs(parseFloat(account.total_debits || 0)) + Math.abs(parseFloat(account.total_credits || 0));
                    total += accountAmount;
                }
            });
        }
        
        // Add totals from all child groups recursively
        if (group.children && Array.isArray(group.children)) {
            group.children.forEach(childGroup => {
                total += calculateGroupTotalWithChildren(childGroup);
            });
        }
        
        return total;
    };

    // Enhanced function to calculate section totals (display all groups as received, no deduplication)
    const calculateSectionTotal = (groups) => {
        if (!groups || !Array.isArray(groups)) return 0;
        
        return groups.reduce((total, group) => {
            return total + calculateGroupTotalWithChildren(group);
        }, 0);
    };

    // Helper to get revenue section (handles API typo)
    const getRevenueSection = () => {
        if (!reportData?.structure) return null;
        return reportData.structure.revenue_section || reportData.structure.revenew_section ||reportData.structure.reveneu_section ||  null;
    };

    // Helper function to get calculated totals for profit/loss calculations
    const getCalculatedTotals = () => {
        if (!reportData?.structure) {
            return {
                totalRevenue: 0,
                totalExpenses: 0,
                netProfit: 0,
                profitMargin: 0,
                isProfit: false
            };
        }

        const totalRevenue = calculateSectionTotal(getRevenueSection()?.groups || []);
        const totalExpenses = calculateSectionTotal(reportData.structure.expense_section?.groups || []);
        const netProfit = totalRevenue - totalExpenses;
        const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

        // Debug logging
        console.log('=== PROFIT CALCULATION DEBUG ===');
        console.log('Calculated Total Revenue:', totalRevenue);
        console.log('API Total Revenue:', Math.abs(reportData.structure.summary?.total_revenue || 0));
        console.log('Calculated Total Expenses:', totalExpenses);
        console.log('API Total Expenses:', Math.abs(reportData.structure.summary?.total_expenses || 0));
        console.log('Calculated Net Profit (Revenue - Expenses):', netProfit);
        console.log('API Net Profit:', reportData.structure.summary?.net_profit_loss || 0);
        console.log('Profit Margin:', profitMargin + '%');
        console.log('Is Profit:', netProfit >= 0);

        return {
            totalRevenue,
            totalExpenses,
            netProfit,
            profitMargin,
            isProfit: netProfit >= 0
        };
    };

    const renderAccountGroup = (group, sectionType, level = 0, groupIndex = 0, parentKey = '') => {
        if (!group) return null;
        
        // Create unique key that includes the index to handle duplicates  
        const groupKey = parentKey ? `${parentKey}_${groupIndex}` : `${sectionType}_${group.group_id}_${groupIndex}`;
        const isExpanded = expandedGroups[groupKey];
        
        // Check for valid accounts (not null and has ID)
        const hasAccounts = group.accounts && group.accounts.length > 0 && 
            group.accounts.some(acc => acc && acc.account_id && acc.account_name);
        const hasChildren = group.children && group.children.length > 0;
        
        // Calculate the total amount including all children
        const totalWithChildren = calculateGroupTotalWithChildren(group);
        
        // Calculate indentation based on nesting level
        const baseIndent = 20;
        const levelIndent = level * 20;
        const totalIndent = baseIndent + levelIndent;
        
        return (
            <React.Fragment key={groupKey}>
                <tr 
                    style={{ 
                        backgroundColor: level === 0 ? '#e9ecef' : level === 1 ? '#f1f3f4' : '#f8f9fa',
                        cursor: (hasAccounts || hasChildren) ? 'pointer' : 'default'
                    }}
                    onClick={(hasAccounts || hasChildren) ? () => toggleGroup(groupKey) : undefined}
                >
                    <td style={{ 
                        paddingLeft: `${totalIndent}px`,
                        fontWeight: level === 0 ? 'bold' : level === 1 ? '600' : 'normal',
                        borderLeft: level > 0 ? '2px solid #dee2e6' : 'none'
                    }}>
                        {(hasAccounts || hasChildren) && (
                            <span className="me-2">
                                {isExpanded ? <FaMinus className="text-danger" size={12} /> : <FaPlus className="text-success" size={12} />}
                            </span>
                        )}
                        {group.group_name}
                        <small className="ms-2 text-muted">
                            (ID: {group.group_id}, {(group.accounts || []).filter(acc => acc && acc.account_id).length} accounts)
                        </small>
                        {group.hierarchy_path && (
                            <><br />
                            <small className="text-muted" style={{ fontSize: '0.75em' }}>
                                Path: {group.hierarchy_path}
                            </small></>
                        )}
                    </td>
                    <td style={{ 
                        textAlign: 'right', 
                        fontWeight: level === 0 ? 'bold' : level === 1 ? '600' : 'normal',
                        color: level === 0 ? '#495057' : '#6c757d'
                    }}>
                        {formatCurrency(totalWithChildren)}
                        {hasChildren && level === 0 && (
                            <><br />
                            <small className="text-muted" style={{ fontSize: '0.75em' }}>
                                Direct: {formatCurrency(Math.abs(group.group_total || 0))}
                            </small></>
                        )}
                        <br />
                        <small className="text-muted" style={{ fontSize: '0.7em' }}>
                            API: {group.group_total || 0}
                        </small>
                    </td>
                </tr>
                
                {/* Render account details when expanded */}
                {hasAccounts && isExpanded && (
                    <>
                        {group.accounts
                            .filter(account => account && account.account_id && account.account_name)
                            .map((account, accountIndex) => (
                            <tr key={`${groupKey}_acc_${account.account_id}_${accountIndex}`} style={{ backgroundColor: '#f8f9fa' }}>
                                <td style={{ 
                                    paddingLeft: `${totalIndent + 20}px`,
                                    fontSize: '0.9em',
                                    borderLeft: '2px solid #dee2e6'
                                }}>
                                    <strong className="text-primary">{account.account_code}</strong> - {account.account_name}
                                    <br />
                                    <small className="text-muted">
                                        <span className="text-danger">Debits: {formatCurrency(account.total_debits)}</span> | 
                                        <span className="text-success">Credits: {formatCurrency(account.total_credits)}</span>
                                        {account.amount !== null && (
                                            <> | <span className="text-info">Amount: {formatCurrency(Math.abs(account.amount))}</span></>
                                        )}
                                    </small>
                                </td>
                                <td style={{ 
                                    textAlign: 'right',
                                    fontSize: '0.9em',
                                    color: '#6c757d'
                                }}>
                                    <strong className={
                                        sectionType === 'revenue' 
                                            ? 'text-success'
                                            : 'text-danger'
                                    }>
                                        {(() => {
                                            // Use amount field if available, otherwise calculate from debits/credits
                                            const displayAmount = account.amount !== null ? 
                                                Math.abs(account.amount) :
                                                Math.abs(parseFloat(account.total_debits || 0)) + Math.abs(parseFloat(account.total_credits || 0));
                                            return formatCurrency(displayAmount);
                                        })()}
                                    </strong>
                                    <br />
                                    <small className="text-muted" style={{ fontSize: '0.7em' }}>
                                        API: {account.amount !== null ? account.amount : 'N/A'}
                                    </small>
                                </td>
                            </tr>
                        ))}
                    </>
                )}
                
                {/* Render child groups recursively with increased nesting level */}
                {hasChildren && isExpanded && (
                    <>
                        {group.children.map((childGroup, childIndex) => 
                            renderAccountGroup(childGroup, sectionType, level + 1, childIndex, groupKey)
                        )}
                    </>
                )}
            </React.Fragment>
        );
    };

    return (
        <>
            <Header />
            <Page title='Profit & Loss Statement' breadcrumbs='Home / Financial / Profit & Loss'>
                <Row className='p-3'>
                    <Col xs={12}>
                        <Card className='mb-3' color="primary" outline>
                            <CardBody>
                                <h6 className='mb-3'>
                                    <FaChartLine className='me-2' />
                                    Search Criteria
                                </h6>
                                <Row>
                                    <Col md={4}>
                                        <label className='form-label'>From Date</label>
                                        <Input 
                                            type='date' 
                                            value={fromDate} 
                                            onChange={handleFromDateChange}
                                            max={toDate || undefined}
                                            className={dateError ? 'is-invalid' : ''}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <label className='form-label'>To Date</label>
                                        <Input 
                                            type='date' 
                                            value={toDate} 
                                            onChange={handleToDateChange}
                                            min={fromDate || undefined}
                                            className={dateError ? 'is-invalid' : ''}
                                        />
                                    </Col>
                                    <Col md={4} className='d-flex align-items-end'>
                                        <Button 
                                            className='text-white me-2' 
                                            color='primary' 
                                            size="sm" 
                                            onClick={search_handler}
                                            disabled={!!dateError || !fromDate || !toDate}
                                        > 
                                            <FaSearch className='me-1' size={16} /> Generate Report
                                        </Button>
                                        <Button className='text-white' color='secondary' size="sm" onClick={reset_handler}> 
                                            Reset
                                        </Button>
                                    </Col>
                                </Row>
                                {dateError && (
                                    <Row className='mt-2'>
                                        <Col xs={12}>
                                            <div className='text-danger small'>
                                                <i className="fa fa-exclamation-triangle me-1"></i>
                                                {dateError}
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </CardBody>
                        </Card>

                        {/* Profit & Loss Content */}
                        {reportData ? (
                            <>
                                {/* Side by Side Layout - Revenue & Expenses */}
                                <Row className='g-4'>
                                    {/* Revenue Section - Left Column */}
                                    <Col lg={6}>
                                        <Card className='h-100 shadow-sm border-0'>
                                            <div className='card-header bg-white border-bottom py-3'>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className='mb-0 text-dark fw-bold'>
                                                        {getRevenueSection()?.section_name || 'REVENUE'}
                                                    </h5>
                                                    <span className='fw-bold text-success fs-6'>
                                                        {(() => {
                                                            const revenueTotal = calculateSectionTotal(getRevenueSection()?.groups || []);
                                                            return formatCurrency(revenueTotal);
                                                        })()}
                                                        {reportData.structure.summary?.total_revenue && (
                                                            <><br />
                                                            <small className="text-muted" style={{ fontSize: '0.75em' }}>
                                                                API: {formatCurrency(Math.abs(reportData.structure.summary.total_revenue))}
                                                            </small></>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            <CardBody className='p-0'>
                                                <div className='table-responsive'>
                                                    <Table hover size="sm" className="mb-0">
                                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ borderTop: 'none', fontWeight: 'bold', padding: '12px 20px' }}>Account</th>
                                                                <th style={{ borderTop: 'none', textAlign: 'right', fontWeight: 'bold', padding: '12px 20px' }}>Amount</th>
                                                            </tr>
                                                        </thead>                                        <tbody>
                                            {(getRevenueSection()?.groups || []).length > 0 ? (
                                                (getRevenueSection()?.groups || [])
                                                    .map((group, index) => renderAccountGroup(group, 'revenue', 0, index))
                                            ) : (
                                                <tr>
                                                    <td colSpan="2" className="text-center text-muted py-4">
                                                        No revenue groups available
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                                    </Table>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>

                                    {/* Expense Section - Right Column */}
                                    <Col lg={6}>
                                        <Card className='h-100 shadow-sm border-0'>
                                            <div className='card-header bg-white border-bottom py-3'>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className='mb-0 text-dark fw-bold'>
                                                        {reportData.structure.expense_section?.section_name || 'EXPENSES'}
                                                    </h5>
                                                    <span className='fw-bold text-danger fs-6'>
                                                        {(() => {
                                                            const expenseTotal = calculateSectionTotal(reportData.structure.expense_section?.groups || []);
                                                            return formatCurrency(expenseTotal);
                                                        })()}
                                                        {reportData.structure.summary?.total_expenses && (
                                                            <><br />
                                                            <small className="text-muted" style={{ fontSize: '0.75em' }}>
                                                                API: {formatCurrency(Math.abs(reportData.structure.summary.total_expenses))}
                                                            </small></>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            <CardBody className='p-0'>
                                                <div className='table-responsive'>
                                                    <Table hover size="sm" className="mb-0">
                                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ borderTop: 'none', fontWeight: 'bold', padding: '12px 20px' }}>Account</th>
                                                                <th style={{ borderTop: 'none', textAlign: 'right', fontWeight: 'bold', padding: '12px 20px' }}>Amount</th>
                                                            </tr>
                                                        </thead>                                        <tbody>
                                            {(reportData.structure.expense_section?.groups || []).length > 0 ? (
                                                (reportData.structure.expense_section?.groups || [])
                                                    .map((group, index) => renderAccountGroup(group, 'expense', 0, index))
                                            ) : (
                                                <tr>
                                                    <td colSpan="2" className="text-center text-muted py-4">
                                                        No expense groups available
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                                    </Table>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <Card className='mb-3 shadow-sm border-0'>
                                <CardBody className="text-center py-5">
                                    {loading ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                            <h5 className="text-muted">Loading Profit & Loss...</h5>
                                        </>
                                    ) : (
                                        <>
                                            <FaChartLine size={48} className="text-muted mb-3" />
                                            <h5 className="text-muted">No Profit & Loss Data Available</h5>
                                            <p className="text-muted">
                                                Please select a date range and click "Generate Report" to view the Profit & Loss statement.
                                            </p>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        )}

                        {/* Profit & Loss Summary - Bottom Section */}
                        {reportData && reportData.structure && reportData.structure.summary && (() => {
                            // Calculate totals once to avoid multiple function calls
                            const calculatedTotals = getCalculatedTotals();
                            
                            return (
                                <Card className='mt-4 shadow-sm border-0'>
                                    <CardBody className='p-4'>
                                        <h5 className='mb-4 text-dark fw-bold'>
                                            Profit & Loss Summary
                                            <small className="ms-2 text-muted">
                                                (Generated: {reportData.generated_at ? new Date(reportData.generated_at).toLocaleString() : 'N/A'})
                                            </small>
                                        </h5>
                                        <Row className='g-3'>
                                            <Col md={3}>
                                                <div className='p-3 bg-light rounded'>
                                                    <h6 className='text-success fw-bold'>Total Revenue</h6>
                                                    <h4 className='text-success mb-0'>
                                                        {formatCurrency(calculatedTotals.totalRevenue)}
                                                    </h4>
                                                    <small className="text-muted">
                                                        ({reportData.structure.summary.total_revenue_accounts || 0} accounts)
                                                        {Math.abs(calculatedTotals.totalRevenue - Math.abs(reportData.structure.summary.total_revenue || 0)) > 0.01 && (
                                                            <><br />API: {formatCurrency(Math.abs(reportData.structure.summary.total_revenue || 0))}</>
                                                        )}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className='p-3 bg-light rounded'>
                                                    <h6 className='text-danger fw-bold'>Total Expenses</h6>
                                                    <h4 className='text-danger mb-0'>
                                                        {formatCurrency(calculatedTotals.totalExpenses)}
                                                    </h4>
                                                    <small className="text-muted">
                                                        ({reportData.structure.summary.total_expense_accounts || 0} accounts)
                                                        {Math.abs(calculatedTotals.totalExpenses - Math.abs(reportData.structure.summary.total_expenses || 0)) > 0.01 && (
                                                            <><br />API: {formatCurrency(Math.abs(reportData.structure.summary.total_expenses || 0))}</>
                                                        )}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className={`p-3 rounded ${calculatedTotals.isProfit ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                                                    <h6 className="fw-bold">Net {calculatedTotals.isProfit ? 'Profit' : 'Loss'}</h6>
                                                    <h4 className="mb-0">
                                                        {formatCurrency(Math.abs(calculatedTotals.netProfit))}
                                                    </h4>
                                                    <small className="text-white">
                                                        ₹{calculatedTotals.totalRevenue.toFixed(2)} - ₹{calculatedTotals.totalExpenses.toFixed(2)}
                                                        {Math.abs(calculatedTotals.netProfit - (reportData.structure.summary.net_profit_loss || 0)) > 0.01 && (
                                                            <><br />API: {formatCurrency(Math.abs(reportData.structure.summary.net_profit_loss || 0))}</>
                                                        )}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className='p-3 bg-light rounded'>
                                                    <h6 className='text-info fw-bold'>Profit Margin</h6>
                                                    <h4 className={`mb-0 ${calculatedTotals.isProfit ? 'text-success' : 'text-danger'}`}>
                                                        {calculatedTotals.profitMargin.toFixed(2)}%
                                                    </h4>
                                                    <small className="text-muted">
                                                        ({formatCurrency(Math.abs(calculatedTotals.netProfit))} ÷ {formatCurrency(calculatedTotals.totalRevenue)}) × 100
                                                    </small>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            );
                        })()}

                        {/* No Data Message */}
                        {!reportData && !loading && (
                            <Card className='mb-3 shadow-sm border-0'>
                                <CardBody className="text-center py-5">
                                    <FaChartLine size={48} className="text-muted mb-3" />
                                    <h5 className="text-muted">No Report Data Available</h5>
                                    <p className="text-muted">
                                        Please select a date range and click "Generate Report" to view the Profit & Loss statement.
                                    </p>
                                </CardBody>
                            </Card>
                        )}

                        {/* Debug Section - Raw API Data */}
                        {reportData && (
                            <Card className='mt-4 shadow-sm border-0'>
                                <CardBody className='p-4'>
                                    <h5 className='mb-4 text-dark fw-bold'>
                                        API Data Structure (Debug Information)
                                    </h5>
                                    <Row className='g-3'>
                                        <Col md={6}>
                                            <div className='p-3 bg-light rounded'>
                                                <h6 className='text-primary fw-bold'>Revenue Groups</h6>
                                                <small className="text-muted d-block" style={{ fontSize: '0.8em', maxHeight: '200px', overflow: 'auto' }}>
                                                    <strong>All Groups (as received):</strong> {getRevenueSection()?.groups?.length || 0}<br />
                                                    <strong>Group IDs:</strong> {(getRevenueSection()?.groups || []).map((g, i) => `${g.group_id}-${g.group_name} [${i}]`).join(', ')}
                                                </small>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className='p-3 bg-light rounded'>
                                                <h6 className='text-danger fw-bold'>Expense Groups</h6>
                                                <small className="text-muted d-block" style={{ fontSize: '0.8em', maxHeight: '200px', overflow: 'auto' }}>
                                                    <strong>All Groups (as received):</strong> {reportData.structure.expense_section?.groups?.length || 0}<br />
                                                    <strong>Group IDs:</strong> {(reportData.structure.expense_section?.groups || []).map((g, i) => `${g.group_id}-${g.group_name} [${i}]`).join(', ')}
                                                </small>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className='mt-3'>
                                        <Col xs={12}>
                                            <div className='p-3 bg-light rounded'>
                                                <h6 className='text-info fw-bold'>Raw API Summary</h6>
                                                <pre style={{ 
                                                    fontSize: '0.7em', 
                                                    color: '#495057', 
                                                    backgroundColor: '#fff', 
                                                    padding: '10px', 
                                                    borderRadius: '3px',
                                                    maxHeight: '300px',
                                                    overflow: 'auto'
                                                }}>
                                                    {JSON.stringify(reportData.structure.summary, null, 2)}
                                                </pre>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Page>
            <Footer />
        </>
    );
};

export default ProfitLoss;
